import { Slider } from "@/components/ui/slider";
import { BUDGET_CATEGORIES, BudgetCategory } from "@/data/developmentSim";
import { AlertTriangle } from "lucide-react";

interface Props {
  pool: number;
  allocation: Record<BudgetCategory, number>; // percent (0-100)
  onChange: (next: Record<BudgetCategory, number>) => void;
}

export default function BudgetBoard({ pool, allocation, onChange }: Props) {
  const total = (Object.values(allocation) as number[]).reduce((a, b) => a + b, 0);
  const remaining = 100 - total;

  const setOne = (k: BudgetCategory, v: number) => {
    onChange({ ...allocation, [k]: v });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-border bg-card/60 p-2 text-[11px]">
        <span className="text-muted-foreground">Budget pool</span>
        <span className="font-mono text-foreground">${pool.toLocaleString()}</span>
      </div>
      <div className={`rounded-xl border p-2 text-[11px] flex items-center justify-between ${
        remaining === 0 ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300"
        : remaining < 0 ? "border-destructive/50 bg-destructive/10 text-destructive"
        : "border-amber-500/40 bg-amber-500/5 text-amber-300"
      }`}>
        <span>Allocated</span>
        <span className="font-mono">{total}% (remaining {remaining}%)</span>
      </div>

      <div className="space-y-3">
        {BUDGET_CATEGORIES.map((c) => {
          const v = allocation[c.key];
          const dollars = Math.round(pool * v / 100);
          const under = v < c.underWarn;
          return (
            <div key={c.key} className="rounded-xl border border-border bg-card/60 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading text-xs font-bold text-foreground">{c.label}</p>
                  <p className="text-[10px] text-muted-foreground">{c.help}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-[12px] text-foreground">{v}%</p>
                  <p className="font-mono text-[10px] text-muted-foreground">${dollars.toLocaleString()}</p>
                </div>
              </div>
              <Slider
                value={[v]} min={0} max={50} step={1}
                onValueChange={([nv]) => setOne(c.key, nv)}
              />
              {under && (
                <div className="flex items-center gap-1.5 text-[10px] text-amber-400">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Under-allocated: {c.warnText}</span>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground/70 italic">Suggested: {c.suggested}%</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}